import { ApiResult } from '@e3dc-react/shell/Types/api'
import { ShopCheckoutSideBarResponse, SmartMeterModalModelResponse } from '../models'
import { ProductType, ShopAuthFetchWithResponse } from '../types'
import { StatusCodes } from '@e3dc-react/shell/Libs'
import { SmartMeterFormValues } from '../components/SystemConfiguration/SmartMeter/SmartMeterForm'
import { combineSmartMeterDateTime } from '../utils/smart-meter'

/*
 * This endpoint get the list of available SmartMeters
 */
export const fetchSmartMeters = async (shopFetch: ShopAuthFetchWithResponse): Promise<ApiResult<SmartMeterModalModelResponse>> => {
  const payload = {
    filter: [
      {
        type: 'equals',
        field: 'e3dcData.productType',
        value: ProductType.SmartMeter,
      },
      {
        type: 'equals',
        field: 'e3dcData.useShopPrice',
        value: true,
      },
    ],
    includes: {},
    associations: {
      media: {
        associations: {
          media: {},
        },
      },
    },
  }

  try {
    const response = await shopFetch<SmartMeterModalModelResponse>({
      path: 'product',
      method: 'GET',
      criteria: payload,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<SmartMeterModalModelResponse>(response.data, true)
    }

    return new ApiResult<SmartMeterModalModelResponse>(null)
  } catch (error) {
    return new ApiResult<SmartMeterModalModelResponse>(null)
  }
}

/*
 * This endpoint upload a smart meter contract
 */
export const uploadSmartMeterContract = async (
  productId: string,
  pdf: Blob,
  contractData: SmartMeterFormValues,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const formData = new FormData()

    formData.append('file', pdf)
    const dateTimeCombined = combineSmartMeterDateTime(contractData.targetInstallationDate, contractData.targetInstallationTime)

    const dataEntries = {
      confirmCustomerData: contractData.confirmCustomerData.toString(), // 'true' or 'false'
      firstName: contractData.firstName,
      lastName: contractData.lastName,
      street: contractData.street,
      streetNumber: contractData.streetNumber,
      zipCode: contractData.zipCode,
      city: contractData.city,
      email: contractData.email,
      mechanicName: contractData.mechanicName,
      previousMeterNumber: contractData.previousMeterNumber,
      targetInstallationDate: dateTimeCombined,
      administrationNumber: contractData?.administrationNumber ?? '',
    }

    Object.entries(dataEntries).forEach(([key, value]) => {
      formData.append(key, value)
    })

    const response = await shopFetch<ShopCheckoutSideBarResponse>({
      path: `e3dc/smart-meter/contract/${productId}/upload`,
      method: 'POST',
      payload: formData,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data, true)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}

/*
 * This endpoint deletes a SmartMeter contract
 */
export const deleteSmartMeterContract = async (
  productId: string,
  contractId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const response = await shopFetch<ShopCheckoutSideBarResponse>({
      path: `e3dc/smart-meter/contract/${productId}/${contractId}/delete`,
      method: 'DELETE',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data, true)
    }

    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}

/*
 * This endpoint will return the smart meter document.
 */
export const readSmartMeterContract = async (
  productId: string,
  contractId: string,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<string>> => {
  try {
    const response = await shopFetch<string>({
      path: `e3dc/smart-meter/contract/${productId}/${contractId}/read`,
      method: 'GET',
      responseType: 'arraybuffer',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<string>(response.data, true)
    }

    return new ApiResult<string>(null)
  } catch (error) {
    return new ApiResult<string>(null)
  }
}

/*
 * This endpoint will modify an existing smartmeter
 */
export const modifySmartMeterContract = async (
  productId: string,
  contractId: string,
  pdf: Blob | null,
  contractData: SmartMeterFormValues,
  shopFetch: ShopAuthFetchWithResponse
): Promise<ApiResult<ShopCheckoutSideBarResponse>> => {
  try {
    const formData = new FormData()
    if (pdf) {
      formData.append('file', pdf)
    }
    const dateTimeCombined = combineSmartMeterDateTime(contractData.targetInstallationDate, contractData.targetInstallationTime)
    const dataEntries = {
      confirmCustomerData: contractData.confirmCustomerData.toString(),
      firstName: contractData.firstName,
      lastName: contractData.lastName,
      street: contractData.street,
      streetNumber: contractData.streetNumber,
      zipCode: contractData.zipCode,
      city: contractData.city,
      email: contractData.email,
      mechanicName: contractData.mechanicName,
      previousMeterNumber: contractData.previousMeterNumber,
      targetInstallationDate: dateTimeCombined,
      administrationNumber: contractData?.administrationNumber ?? '',
    }

    Object.entries(dataEntries).forEach(([key, value]) => {
      formData.append(key, value)
    })
    const response = await shopFetch<ShopCheckoutSideBarResponse>({
      path: `e3dc/smart-meter/contract/${productId}/${contractId}/replace`,
      method: 'POST',
      payload: formData,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<ShopCheckoutSideBarResponse>(response.data, true)
    }
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  } catch (error) {
    return new ApiResult<ShopCheckoutSideBarResponse>(null)
  }
}
